import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas, getMouseCanvas } from "../GenericComponent";
import { hexToRGBA, getStrokeDasharray } from "../utils";

class HiLoActivatorSeries extends Component {
  constructor(props) {
    super(props);
    this.yAccessorLong = this.yAccessorLong.bind(this);
    this.yAccessorShort = this.yAccessorShort.bind(this);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.getSegments = this.getSegments.bind(this);
    this.normalizeStroke = this.normalizeStroke.bind(this);
    this.isHover = this.isHover.bind(this);
  }

  yAccessorLong(d) {
    if (!d) return undefined;
    const { yAccessor } = this.props;
    const node = yAccessor ? yAccessor(d) : undefined;
    return node && node.P1 != null ? node.P1 : undefined;
  }

  yAccessorShort(d) {
    if (!d) return undefined;
    const { yAccessor } = this.props;
    const node = yAccessor ? yAccessor(d) : undefined;
    return node && node.P2 != null ? node.P2 : undefined;
  }

  render() {
    const {
      className,
      clip,
      longStroke,
      shortStroke,
      onContextMenu,
      onDoubleClick,
      onHover,
      onUnHover,
      onClick,
      highlightOnHover,
      hoverTolerance,
    } = this.props;

    const longStrokeProps = this.normalizeStroke(longStroke, {
      color: "#2ecc71",
      width: 1,
      dash: "Solid",
      opacity: 1,
    });

    const shortStrokeProps = this.normalizeStroke(shortStroke, {
      color: "#ff4d4d",
      width: 1,
      dash: "Solid",
      opacity: 1,
    });

    const needsHover =
      highlightOnHover ||
      onHover ||
      onUnHover ||
      onContextMenu ||
      onDoubleClick ||
      onClick;
    const hoverProps = needsHover
      ? {
          isHover: this.isHover,
          drawOn: ["mousemove", "pan"],
          canvasToDraw: getMouseCanvas,
        }
      : {
          drawOn: ["pan"],
          canvasToDraw: getAxisCanvas,
        };

    return (
      <g className={className}>
        <GenericChartComponent
          clip={clip}
          svgDraw={(moreProps) =>
            this.renderSVG(moreProps, longStrokeProps, shortStrokeProps)
          }
          canvasDraw={(ctx, moreProps) =>
            this.drawOnCanvas(ctx, moreProps, longStrokeProps, shortStrokeProps)
          }
          onHover={onHover}
          onUnHover={onUnHover}
          onClickWhenHover={onClick}
          onDoubleClickWhenHover={onDoubleClick}
          onContextMenuWhenHover={onContextMenu}
          {...hoverProps}
        />
      </g>
    );
  }
}

HiLoActivatorSeries.prototype.normalizeStroke = function (stroke, fallback) {
  if (typeof stroke === "string") {
    return { ...fallback, color: stroke };
  }
  return {
    color: stroke?.color || fallback.color,
    width: stroke?.width ?? fallback.width,
    dash: stroke?.dash || fallback.dash,
    opacity: stroke?.opacity ?? fallback.opacity,
  };
};

HiLoActivatorSeries.prototype.getSegments = function (moreProps, accessor) {
  const {
    plotData,
    xScale,
    xAccessor,
    chartConfig: { yScale, origin },
  } = moreProps;
  const segments = [];
  let prev = null;
  const tolerance = 0.01;

  const positionFor = (index) => {
    const center = xScale(xAccessor(plotData[index]));
    const prevCenter =
      index > 0 ? xScale(xAccessor(plotData[index - 1])) : null;
    const nextCenter =
      index < plotData.length - 1
        ? xScale(xAccessor(plotData[index + 1]))
        : null;

    let left;
    let right;

    if (prevCenter != null) {
      left = (center + prevCenter) / 2;
    } else {
      const forward = nextCenter != null ? nextCenter : center + 1;
      left = center - Math.max(1, forward - center);
    }

    if (nextCenter != null) {
      right = (center + nextCenter) / 2;
    } else if (prevCenter != null) {
      right = center + Math.max(1, center - prevCenter);
    } else {
      right = center + 1;
    }

    return { left, right };
  };

  for (let i = 0; i < plotData.length; i += 1) {
    const datum = plotData[i];
    const value = accessor(datum);
    if (!Number.isFinite(value)) {
      prev = null;
      continue;
    }

    const { left, right } = positionFor(i);
    const y = yScale(value);

    if (!prev) {
      segments.push({ type: "M", x: left, y, rawX: left, rawY: y });
    } else {
      const delta = Math.abs(prev.right - left);
      if (delta > tolerance) {
        segments.push({
          type: "L",
          x: left,
          y: prev.y,
          rawX: left,
          rawY: prev.y,
        });
      }
      if (Math.abs(prev.y - y) > tolerance) {
        segments.push({ type: "L", x: left, y, rawX: left, rawY: y });
      }
    }
    segments.push({ type: "L", x: right, y, rawX: right, rawY: y });
    prev = { right, y };
  }

  return segments;
};

HiLoActivatorSeries.prototype.renderSVG = function (
  moreProps,
  longStrokeProps,
  shortStrokeProps,
) {
  const longSegments = this.getSegments(
    moreProps,
    this.yAccessorLong.bind(this),
  );
  const shortSegments = this.getSegments(
    moreProps,
    this.yAccessorShort.bind(this),
  );

  const buildPath = (segments) => {
    if (!segments.length) return null;
    return segments.map((cmd) => `${cmd.type} ${cmd.x} ${cmd.y}`).join(" ");
  };

  const paths = [];
  const longPath = buildPath(longSegments);
  if (longPath) {
    paths.push(
      <path
        key="long"
        d={longPath}
        fill="none"
        stroke={longStrokeProps.color}
        strokeWidth={longStrokeProps.width}
        strokeOpacity={longStrokeProps.opacity}
        strokeDasharray={getStrokeDasharray(longStrokeProps.dash)}
      />,
    );
  }

  const shortPath = buildPath(shortSegments);
  if (shortPath) {
    paths.push(
      <path
        key="short"
        d={shortPath}
        fill="none"
        stroke={shortStrokeProps.color}
        strokeWidth={shortStrokeProps.width}
        strokeOpacity={shortStrokeProps.opacity}
        strokeDasharray={getStrokeDasharray(shortStrokeProps.dash)}
      />,
    );
  }

  return <g>{paths}</g>;
};

HiLoActivatorSeries.prototype.drawOnCanvas = function (
  ctx,
  moreProps,
  longStrokeProps,
  shortStrokeProps,
) {
  const drawCommands = (segments, stroke) => {
    if (!segments.length) return;
    ctx.beginPath();
    segments.forEach((cmd) => {
      if (cmd.type === "M") {
        ctx.moveTo(cmd.x, cmd.y);
      } else {
        ctx.lineTo(cmd.x, cmd.y);
      }
    });
    const { hovering } = moreProps;
    ctx.lineWidth = hovering ? (stroke.width + 1) * 1.5 : stroke.width;
    ctx.strokeStyle = hexToRGBA(stroke.color, stroke.opacity);
    ctx.setLineDash(getStrokeDasharray(stroke.dash).split(","));
    ctx.stroke();
    ctx.setLineDash([]);
  };

  drawCommands(
    this.getSegments(moreProps, this.yAccessorLong.bind(this)),
    longStrokeProps,
  );
  drawCommands(
    this.getSegments(moreProps, this.yAccessorShort.bind(this)),
    shortStrokeProps,
  );
};

HiLoActivatorSeries.prototype.isHover = function (moreProps) {
  const {
    highlightOnHover,
    hoverTolerance,
    onHover,
    onUnHover,
    onContextMenu,
    onDoubleClick,
    onClick,
  } = this.props;

  const needsHover =
    highlightOnHover ||
    onHover ||
    onUnHover ||
    onContextMenu ||
    onDoubleClick ||
    onClick;
  if (!needsHover) return false;

  const tolerance = Number.isFinite(hoverTolerance) ? hoverTolerance : 6;
  const {
    mouseXY,
    chartConfig: { origin },
  } = moreProps;
  if (!mouseXY) return false;

  const [mouseXRaw, mouseYRaw] = mouseXY;
  const pointer = {
    x: mouseXRaw + origin[0],
    y: mouseYRaw + origin[1],
  };

  const checkSegments = (segments) => {
    if (!segments.length) return false;
    for (let i = 0; i < segments.length - 1; i += 1) {
      const start = segments[i];
      const end = segments[i + 1];
      if (!start || !end) continue;

      const sx = (start.rawX ?? start.x) + origin[0];
      const sy = (start.rawY ?? start.y) + origin[1];
      const ex = (end.rawX ?? end.x) + origin[0];
      const ey = (end.rawY ?? end.y) + origin[1];

      const dx = ex - sx;
      const dy = ey - sy;
      const len2 = dx * dx + dy * dy;
      if (len2 === 0) {
        if (Math.hypot(pointer.x - sx, pointer.y - sy) <= tolerance)
          return true;
        continue;
      }

      let t = ((pointer.x - sx) * dx + (pointer.y - sy) * dy) / len2;
      t = Math.max(0, Math.min(1, t));
      const px = sx + t * dx;
      const py = sy + t * dy;
      const distance = Math.hypot(pointer.x - px, pointer.y - py);
      if (distance <= tolerance) return true;
    }
    return false;
  };

  const longSegments = this.getSegments(
    moreProps,
    this.yAccessorLong.bind(this),
  );
  if (checkSegments(longSegments)) return true;

  const shortSegments = this.getSegments(
    moreProps,
    this.yAccessorShort.bind(this),
  );
  if (checkSegments(shortSegments)) return true;

  return false;
};

HiLoActivatorSeries.propTypes = {
  className: PropTypes.string,
  clip: PropTypes.bool,
  yAccessor: PropTypes.func.isRequired,
  longStroke: PropTypes.oneOfType([
    PropTypes.shape({
      color: PropTypes.string,
      width: PropTypes.number,
      dash: PropTypes.string,
      opacity: PropTypes.number,
    }),
    PropTypes.string,
  ]),
  shortStroke: PropTypes.oneOfType([
    PropTypes.shape({
      color: PropTypes.string,
      width: PropTypes.number,
      dash: PropTypes.string,
      opacity: PropTypes.number,
    }),
    PropTypes.string,
  ]),
  onContextMenu: PropTypes.func,
  onDoubleClick: PropTypes.func,
  highlightOnHover: PropTypes.bool,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,
  onClick: PropTypes.func,
  hoverTolerance: PropTypes.number,
};

HiLoActivatorSeries.defaultProps = {
  className: "react-stockcharts-hilo-activator-series",
  clip: true,
  longStroke: { color: "#2ecc71", dash: "Solid", width: 1, opacity: 1 },
  shortStroke: { color: "#ff4d4d", dash: "Solid", width: 1, opacity: 1 },
  highlightOnHover: true,
  hoverTolerance: 6,
};

export default HiLoActivatorSeries;
